import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  FieldOpsCustomerListData,
  useLazyGetBeatPlanCustomerListQuery,
  getWorkspaceId,
  useAppSelector,
  getUserData,
  dispatch,
  updateSelectedItemIds,
  useLazyGetRouteScheduleQuery,
  RouteScheduleResponse,
  Route,
  updateExtraDetails,
  addCatalogDownloadItems,
  updateAllCustomerProfileSynced,
  getIsSelectedAll,
  updateIsSelectedAll,
  clearSelectedItemIds,
  getAvailableCustomersCount,
  updateBeatPlanScheduleIds,
  updateCurrentCustomer,
  removeSearchKey,
  getCustomerProfileSyncStatusForCustomer,
  useGetCustomerReportMutation,
  FieldOpsCustomerListResponse,
  CustomersReportResponse,
  FieldOpsCustomerWithMetrics,
} from '@zonofi/zono-money-store';
import { useDebounceSearch, useLoadAllApiDataV2 } from '../../hooks';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FIELD_OPS_ROUTES } from '../../constants';
import { NotificationData, useNotificationToast } from '../../notification';
import { ChipFilterSection } from '../../types';
import { useCustomerProfileFetcher } from './use-customer-profile-fetcher';
import { customApiQueue } from '../../queue/request';
import { syncCacheWithRedux } from '../../cache/sync-cache-with-redux';
import { profileInFlightRequests } from '../../cache/in-flight-requests';

let customerMetricsCache: Record<string, CustomersReportResponse> = {};

export const useFieldOpsCustomers = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const paramTab = Number(searchParams?.get('tab'));

  const [showCalender, setShowCalender] = useState<boolean>(false);
  const [showCheckboxes, setShowCheckboxes] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const bulkProfileSyncStartedRef = useRef<boolean>(false);
  const metricsMapRef = useRef<Record<string, CustomersReportResponse>>({});
  const [metricsVersion, setMetricsVersion] = useState(0);
  const { onShowToast, onCloseToast } = useNotificationToast();

  const isSelectedAll = useAppSelector(getIsSelectedAll);
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);
  const teamMemberId = useAppSelector(getUserData).inviteId;
  const availableCustomersCount = useAppSelector(getAvailableCustomersCount);
  const customerProfileSyncStatus = useAppSelector(
    getCustomerProfileSyncStatusForCustomer
  );
  const { debouncedSearchValue } = useDebounceSearch();

  const [getFieldOpsCustomerListApi, getFieldOpsCustomerListResponse] =
    useLazyGetBeatPlanCustomerListQuery();
  const [getFieldOpsRouteListAPI, getFieldOpsRouteListAPIResponse] =
    useLazyGetRouteScheduleQuery();

  const { fetchProfileLowPriority } = useCustomerProfileFetcher();
  const [getCustomerReportAPI] = useGetCustomerReportMutation();

  useEffect(() => {
    return () => {
      dispatch(removeSearchKey());
    };
  }, []);

  useEffect(() => {
    if (!isNaN(paramTab)) {
      setSelectedTab(paramTab);
    }
  }, [paramTab]);

  useEffect(() => {
    metricsMapRef.current = customerMetricsCache;
  }, []);

  const onActivityClick = () => {
    navigate(FIELD_OPS_ROUTES.SYNC);
  };

  const onClickRoute = (route: Route, beatPlanId?: string) => {
    dispatch(
      updateExtraDetails({
        routeName: route.name,
        routeId: route.id,
      })
    );
    navigate(
      `${FIELD_OPS_ROUTES.ROUTE_DETAILS?.replace(':routeId', route?.id)}?tab=${1}&beatPlanId=${beatPlanId ?? ''}`
    );
  };

  const onSelectItem = (id: string) => {
    dispatch(updateSelectedItemIds(id));
  };

  const onClickCustomer = ({
    id: customerId,
    name: customerName,
    companyName,
    phone,
    beatPlanScheduleCustomerId,
    beatPlanScheduleRouteId,
    physicalAddress,
  }: FieldOpsCustomerWithMetrics) => {
    customApiQueue.cancelTaskById(customerId);
    dispatch(
      updateExtraDetails({
        customerId: customerId,
        customerName: customerName,
        companyName: companyName,
        ...(phone && { mobile: phone }),
        ...(physicalAddress && {
          address: physicalAddress,
        }),
      })
    );
    dispatch(
      updateBeatPlanScheduleIds({
        beatPlanScheduleCustomerId,
        beatPlanScheduleRouteId,
      })
    );
    navigate(
      `${FIELD_OPS_ROUTES.CUSTOMER_PROFILE?.replace(':customerId', customerId)}`
    );
    dispatch(
      updateCurrentCustomer({
        customerId,
        customerName: companyName ?? customerName,
        phone,
      })
    );
  };

  const displayNotification = useCallback((data: NotificationData) => {
    onShowToast({
      body: data.body,
      title: data.title,
      type: 'download',
    });

    setTimeout(() => {
      onCloseToast();
    }, 8000);
  }, []);

  const fetchFieldOpsCustomerList = useCallback(
    (params: { pageNo: number; maxPageSize: number }) => {
      if (selectedTab === 0) {
        if (params.pageNo === 1) {
          metricsMapRef.current = {};
          customerMetricsCache = {};
          setMetricsVersion(0);
        }

        getFieldOpsCustomerListApi({
          sellerWorkspaceId: sellerWorkspaceId,
          teamMemberId: teamMemberId || '',
          pageNo: params.pageNo,
          pageSize: params.maxPageSize,
        })
          .unwrap()
          .then((response) => {
            const customerIds = response?.data.map((x) => x.id) || [];

            const missingIds = customerIds.filter(
              (id) => !metricsMapRef.current[id]
            );

            if (missingIds.length === 0) return;

            getCustomerReportAPI({
              params: { sellerWorkspaceId },
              body: {
                customerIds: missingIds,
                sellerworkspaceid: sellerWorkspaceId,
              },
            })
              .unwrap()
              .then((metrics) => {
                metrics.forEach((item) => {
                  metricsMapRef.current[item.inviteId] = item;
                  customerMetricsCache[item.inviteId] = item;
                });
                setMetricsVersion((prev) => prev + 1);
              })
              .catch((err) => {
                console.error('Error fetching metrics: ', err);
              });
          })
          .catch((err) => {
            console.error('Error fetching customers: ', err);
          });
      }
    },
    [selectedTab, sellerWorkspaceId]
  );

  const fetchFieldOpsRouteList = useCallback(
    (params: { pageNo: number; maxPageSize: number; refresh?: boolean }) => {
      if (selectedTab === 1) {
        getFieldOpsRouteListAPI({
          sellerWorkspaceId: sellerWorkspaceId,
          teamMemberId: teamMemberId || '',
          pageNo: params.refresh ? 1 : params.pageNo,
          pageSize: params.maxPageSize,
        });
      }
    },
    [selectedTab]
  );

  const tabs: ChipFilterSection[] = useMemo(
    () => [
      {
        items: [
          {
            id: '0',
            value: 'All Customers',
            selected: selectedTab === 0,
          },
          {
            id: '1',
            value: 'Sort By Route',
            selected: selectedTab === 1,
          },
        ],
        onItemPress: ({ id }: { id: string }) => {
          if (selectedTab !== Number(id)) {
            dispatch(removeSearchKey());
            setShowSearch(false);
          }
          setSelectedTab(Number(id));
          setSearchParams(`?tab=${id}`);
        },
      },
    ],
    [selectedTab]
  );

  const {
    data: fieldOpsCustomer,
    isLoading: isCustomerLoading,
    completed,
    raw: rawFieldOpsCustomer,
    refetch: refetchFieldOpsCustomerList,
  } = useLoadAllApiDataV2<
    FieldOpsCustomerListData,
    FieldOpsCustomerListResponse
  >({
    fetchDataApiCall: fetchFieldOpsCustomerList,
    apiResponse: getFieldOpsCustomerListResponse,
    transformer: {
      getData: (res) => res.data,
      getPagination: (res) => ({ hasNextPage: res.pagination.hasNextPage }),
    },
    uniqueKey: 'id',
    maxPageSize: 100,
    dependencies: [selectedTab],
    searchText: debouncedSearchValue,
    searchPaths: ['name', 'companyName', 'phone'],
  });

  const {
    data: routeData,
    isLoading: isRouteLoading,
    refetch: refetchFieldOpsRouteList,
  } = useLoadAllApiDataV2<RouteScheduleResponse, RouteScheduleResponse[]>({
    fetchDataApiCall: fetchFieldOpsRouteList,
    apiResponse: getFieldOpsRouteListAPIResponse,
    transformer: {
      getData: (res) => res,
      getPagination: (res) => ({
        hasNextPage: res.length === 100,
      }),
    },
    uniqueKey: 'id',
    maxPageSize: 100,
    dependencies: [selectedTab],
    searchText: debouncedSearchValue,
    searchPaths: ['route.name'],
  });

  const fieldOpsCustomerWithMetrics: FieldOpsCustomerWithMetrics[] =
    useMemo(() => {
      if (!fieldOpsCustomer || fieldOpsCustomer.length === 0) return [];

      return fieldOpsCustomer.map((customer) => {
        const metric = metricsMapRef.current[customer.id];
        if (!metric) return customer;

        return {
          ...customer,
          routes: metric.routes
            ? metric.routes.split(',').map((r: string) => r.trim())
            : [],
          segments: metric.segments
            ? metric.segments.split(',').map((s: string) => s.trim())
            : [],
          outstandingamount: metric.outstandingAmount,
          lastinvoicedate: metric.latestInvoiceDate,
        };
      });
    }, [fieldOpsCustomer, metricsVersion]);

  const handleToggleSelectAll = useCallback(() => {
    const next = !isSelectedAll;
    dispatch(updateIsSelectedAll(next));
    if (next) {
      if (
        selectedTab === 0 &&
        completed &&
        fieldOpsCustomerWithMetrics?.length > 0
      ) {
        const allCustomerIds =
          fieldOpsCustomerWithMetrics?.map((customer) => customer?.id) ?? [];
        dispatch(updateSelectedItemIds(allCustomerIds));
      }
      if (selectedTab === 1 && routeData && routeData?.length > 0) {
        dispatch(
          updateSelectedItemIds(routeData?.map((route) => route?.routeId))
        );
      }
    } else {
      dispatch(clearSelectedItemIds());
    }
  }, [
    fieldOpsCustomerWithMetrics,
    selectedTab,
    completed,
    routeData,
    isSelectedAll,
  ]);

  const handleClearSelection = () => {
    dispatch(clearSelectedItemIds());
  };

  const allCustomerProfilesSynced = useMemo(() => {
    if (rawFieldOpsCustomer && rawFieldOpsCustomer.length > 0) {
      return rawFieldOpsCustomer.every((customer) =>
        customerProfileSyncStatus(customer.id)
      );
    }
    return false;
  }, [rawFieldOpsCustomer, customerProfileSyncStatus]);

  useEffect(() => {
    if (
      selectedTab === 0 &&
      completed &&
      (!allCustomerProfilesSynced ||
        (allCustomerProfilesSynced &&
          rawFieldOpsCustomer?.length > availableCustomersCount))
    ) {
      dispatch(
        addCatalogDownloadItems(
          rawFieldOpsCustomer?.map((customer) => customer?.id) || []
        )
      );
    }
  }, [
    selectedTab,
    completed,
    allCustomerProfilesSynced,
    rawFieldOpsCustomer,
    isSelectedAll,
    availableCustomersCount,
  ]);

  useEffect(() => {
    if (selectedTab !== 0 || !completed) return;
    if (!rawFieldOpsCustomer?.length) return;

    let isCancelled = false;

    const run = async () => {
      const ids = rawFieldOpsCustomer.map((c) => c.id);

      // Phase 1: sync redux with cache
      const { profileMissing } = await syncCacheWithRedux(ids);

      if (isCancelled) return;

      // Phase 2: compute what to fetch
      const customersToSync = rawFieldOpsCustomer.filter((customer) => {
        const id = customer.id;

        const isMissingFromCache = profileMissing.includes(id);
        const isNotDownloaded = !customerProfileSyncStatus(id);
        const isLoading = profileInFlightRequests.has(id);

        return (isMissingFromCache || isNotDownloaded) && !isLoading;
      });

      if (customersToSync.length === 0) return;

      displayNotification({
        title: 'Downloading : Customer Profiles',
        body: `Downloading profiles for ${customersToSync.length} customers.`,
      });

      customersToSync.forEach((customer) => {
        fetchProfileLowPriority(customer.id).catch((err) =>
          console.error(
            'Error fetching customer profile (bulk sync): ',
            customer.id,
            err
          )
        );
      });
    };

    run();

    return () => {
      isCancelled = true;
    };
  }, [selectedTab, completed, rawFieldOpsCustomer]);

  useEffect(() => {
    bulkProfileSyncStartedRef.current = false;
  }, [selectedTab]);

  const refetchCurrentTab = useCallback(() => {
    if (selectedTab === 0) {
      metricsMapRef.current = {};
      customerMetricsCache = {};
      setMetricsVersion(0);
      refetchFieldOpsCustomerList();
    } else {
      refetchFieldOpsRouteList();
    }
  }, [selectedTab, refetchFieldOpsCustomerList, refetchFieldOpsRouteList]);

  return {
    showCalender,
    setShowCalender,
    fieldOpsCustomer: fieldOpsCustomerWithMetrics,
    fetchFieldOpsCustomerList,
    isLoading: selectedTab === 0 ? isCustomerLoading : isRouteLoading,
    selectedTab,
    setSelectedTab,
    showCheckboxes,
    setShowCheckboxes,
    tabs,
    onClickCustomer,
    onSelectItem,
    routeData,
    onClickRoute,
    onActivityClick,
    displayNotification,
    handleToggleSelectAll,
    handleClearSelection,
    refetchCurrentTab,
    showSearch,
    setShowSearch,
  };
};
